/**
 * Featured Tracks Service
 * Handles API calls for featuring tracks and managing credits
 */

import axiosInstance from './axios';

const FEATURED_BASE = '/featured';

/**
 * Get user's credit balance
 * @returns {Promise} Credit balance object
 */
export const getCredits = async () => {
  const response = await axiosInstance.get(`${FEATURED_BASE}/credits/balance`);
  return response.data;
};

/**
 * Purchase credits with wallet balance
 * @param {number} credits - Number of credits to purchase
 * @returns {Promise} Purchase result
 */
export const purchaseCredits = async (credits) => {
  const response = await axiosInstance.post(`${FEATURED_BASE}/credits/purchase`, {
    credits
  });
  return response.data;
};

/**
 * Get credit packages and pricing info
 * @returns {Promise} Package information
 */
export const getCreditPackages = async () => {
  const response = await axiosInstance.get(`${FEATURED_BASE}/credits/packages`);
  return response.data;
};

/**
 * Feature a track in the main carousel
 * @param {string} trackId - UUID of the track
 * @param {number} credits - Number of credits to spend (hours)
 * @param {string} startDate - Optional ISO date string for scheduling
 * @returns {Promise} Feature result
 */
export const featureTrack = async (trackId, credits, startDate = null) => {
  const payload = { credits };
  if (startDate) {
    payload.start_date = startDate;
  }

  const response = await axiosInstance.post(
    `${FEATURED_BASE}/tracks/${trackId}/feature`,
    payload
  );
  return response.data;
};

/**
 * Get feature status for a specific track
 * @param {string} trackId - UUID of the track
 * @returns {Promise} Feature status
 */
export const getTrackFeatureStatus = async (trackId) => {
  const response = await axiosInstance.get(
    `${FEATURED_BASE}/tracks/${trackId}/feature-status`
  );
  return response.data;
};

/**
 * Extend the featured duration of a track
 * @param {string} trackId - UUID of the track
 * @param {number} credits - Additional credits to spend
 * @returns {Promise} Extension result
 */
export const extendFeature = async (trackId, credits) => {
  const response = await axiosInstance.patch(
    `${FEATURED_BASE}/tracks/${trackId}/extend`,
    { credits }
  );
  return response.data;
};

/**
 * Cancel a featured track (no refunds)
 * @param {string} trackId - UUID of the track
 * @returns {Promise} Cancellation result
 */
export const cancelFeature = async (trackId) => {
  const response = await axiosInstance.delete(
    `${FEATURED_BASE}/tracks/${trackId}/feature`
  );
  return response.data;
};

/**
 * Get all currently active featured tracks (public endpoint)
 * @returns {Promise} Array of featured tracks
 */
export const getFeaturedTracks = async () => {
  const response = await axiosInstance.get(`${FEATURED_BASE}/featured-tracks`);
  return response.data;
};

/**
 * Get current user's featured tracks (scheduled, active, and ending soon)
 * @returns {Promise} Array of user's featured tracks
 */
export const getMyFeaturedTracks = async () => {
  const response = await axiosInstance.get(`${FEATURED_BASE}/user/featured-tracks`);
  return response.data;
};

export default {
  getCredits,
  purchaseCredits,
  getCreditPackages,
  featureTrack,
  getTrackFeatureStatus,
  extendFeature,
  cancelFeature,
  getFeaturedTracks,
  getMyFeaturedTracks
};
